import { useApiQuery } from '@/hooks/react-query/useApiQuery';
import { useApiMutation } from '@/hooks/react-query/useApiMutation';
import { queryKeys } from '@/hooks/react-query/queryKeys';
import { fundReportService } from '@/services/fundReportService';
import type {
  DueReport,
  FundReport,
  FundReportCreate,
  FundReportListItem,
  ReportDefinition,
  ReportField,
  ReportTemplate,
  ReportTemplateCreate,
  ReportTemplateListItem,
  ReportTemplateSave,
  ReportNarrative,
} from '@/types/fundReport';

export const useFundReportDefinitions = (fundId: string | undefined) =>
  useApiQuery<ReportDefinition[]>(
    queryKeys.fundReports.definitions(fundId ?? ''),
    () => fundReportService.getReportDefinitions(fundId!),
    { enabled: !!fundId }
  );

export const useFundReports = (fundId: string | undefined) =>
  useApiQuery<FundReportListItem[]>(
    queryKeys.fundReports.list(fundId ?? ''),
    () => fundReportService.getReports(fundId!),
    { enabled: !!fundId }
  );

/** Closed periods with no report behind them, newest first. */
export const useDueFundReports = (fundId: string | undefined) =>
  useApiQuery<DueReport[]>(
    queryKeys.fundReports.due(fundId ?? ''),
    () => fundReportService.getDueReports(fundId!),
    { enabled: !!fundId }
  );

export const useFundReport = (
  fundId: string | undefined,
  reportId: string | undefined
) =>
  useApiQuery<FundReport>(
    queryKeys.fundReports.detail(fundId ?? '', reportId ?? ''),
    () => fundReportService.getReport(fundId!, reportId!),
    { enabled: !!fundId && !!reportId }
  );

export const useGenerateFundReport = (fundId: string) =>
  useApiMutation<{ id: string }, Error, FundReportCreate>(
    (data) => fundReportService.generateReport(fundId, data),
    {
      mutationKey: ['generateFundReport', fundId],
      invalidateKeys: [
        queryKeys.fundReports.list(fundId),
        queryKeys.fundReports.due(fundId),
      ],
      successMessage: 'Report draft ready',
    }
  );

export const useSaveFundReportNarrative = (fundId: string, reportId: string) =>
  useApiMutation<{ id: string }, Error, ReportNarrative>(
    (narrative) => fundReportService.saveNarrative(fundId, reportId, narrative),
    {
      mutationKey: ['saveFundReportNarrative', reportId],
      invalidateKeys: [queryKeys.fundReports.detail(fundId, reportId)],
      successMessage: 'Saved',
    }
  );

export const useApproveFundReport = (fundId: string, reportId: string) =>
  useApiMutation<{ id: string }, Error, void>(
    () => fundReportService.approveReport(fundId, reportId),
    {
      mutationKey: ['approveFundReport', reportId],
      invalidateKeys: [
        queryKeys.fundReports.detail(fundId, reportId),
        queryKeys.fundReports.list(fundId),
      ],
      successMessage: 'Report approved',
    }
  );

export const usePublishFundReport = (fundId: string, reportId: string) =>
  useApiMutation<{ id: string }, Error, void>(
    () => fundReportService.publishReport(fundId, reportId),
    {
      mutationKey: ['publishFundReport', reportId],
      invalidateKeys: [
        queryKeys.fundReports.detail(fundId, reportId),
        queryKeys.fundReports.list(fundId),
        queryKeys.documents.all,
      ],
      successMessage: 'Report published to the investor portal',
    }
  );

export const useReportTemplates = (fundId: string | undefined) =>
  useApiQuery<ReportTemplateListItem[]>(
    queryKeys.fundReports.templates(fundId ?? ''),
    () => fundReportService.getReportTemplates(fundId!),
    { enabled: !!fundId }
  );

export const useReportTemplate = (
  fundId: string | undefined,
  templateId: string | undefined
) =>
  useApiQuery<ReportTemplate>(
    queryKeys.fundReports.template(fundId ?? '', templateId ?? ''),
    () => fundReportService.getReportTemplate(fundId!, templateId!),
    { enabled: !!fundId && !!templateId }
  );

/** Merge fields with this fund's own numbers, so an author can see what exists. */
export const useReportFields = (
  fundId: string | undefined,
  definitionId: string | undefined
) =>
  useApiQuery<ReportField[]>(
    queryKeys.fundReports.fields(fundId ?? '', definitionId ?? ''),
    () => fundReportService.getReportFields(fundId!, definitionId!),
    { enabled: !!fundId && !!definitionId }
  );

export const useCreateReportTemplate = (fundId: string) =>
  useApiMutation<{ id: string }, Error, ReportTemplateCreate>(
    (data) => fundReportService.createReportTemplate(fundId, data),
    {
      mutationKey: ['createReportTemplate', fundId],
      invalidateKeys: [queryKeys.fundReports.templates(fundId)],
      successMessage: 'Template added',
    }
  );

export const useUpdateReportTemplate = (fundId: string, templateId: string) =>
  useApiMutation<{ id: string }, Error, ReportTemplateSave>(
    (data) => fundReportService.updateReportTemplate(fundId, templateId, data),
    {
      mutationKey: ['updateReportTemplate', fundId, templateId],
      invalidateKeys: [
        queryKeys.fundReports.templates(fundId),
        queryKeys.fundReports.template(fundId, templateId),
      ],
      successMessage: 'Template saved',
    }
  );

/** Make this the template the report type prints for this fund. */
export const usePutTemplateInUse = (fundId: string) =>
  useApiMutation<{ id: string }, Error, string>(
    (templateId) => fundReportService.putTemplateInUse(fundId, templateId),
    {
      mutationKey: ['putTemplateInUse', fundId],
      invalidateKeys: [queryKeys.fundReports.templates(fundId)],
      successMessage: 'This template is now active',
    }
  );

export const useDeleteReportTemplate = (fundId: string) =>
  useApiMutation<void, Error, string>(
    (templateId) => fundReportService.deleteReportTemplate(fundId, templateId),
    {
      mutationKey: ['deleteReportTemplate', fundId],
      invalidateKeys: [queryKeys.fundReports.templates(fundId)],
      successMessage: 'Template deleted',
    }
  );
